import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminNav } from '../../components/admin-nav/admin-nav';
import { BroadcastRecord, NotificationAdminItem, NotificationAdminService, UserSearchResult } from '../../services/notification-admin.service';

type Tab = 'broadcast' | 'individual' | 'history' | 'list';

@Component({
  selector: 'app-notifications-admin',
  imports: [CommonModule, FormsModule, AdminNav],
  templateUrl: './notifications-admin.html',
  styleUrl: './notifications-admin.scss',
})
export class NotificationsAdmin implements OnInit {

  activeTab: Tab = 'individual';

  // Broadcast form
  broadcastTitle = '';
  broadcastContent = '';
  broadcastSending = false;
  broadcastSuccess = false;
  broadcastError = '';

  // Individual form
  individualTitle = '';
  individualContent = '';
  individualUserId = '';
  individualSending = false;
  individualSuccess = false;
  individualError = '';

  // User search (autocomplete for individual tab)
  userSearchQuery = '';
  userSearchResults: UserSearchResult[] = [];
  userSearchLoading = false;
  private searchDebounceTimer: any;

  // History
  broadcasts: BroadcastRecord[] = [];
  historyLoading = false;
  historyError = false;

  // Notification list (paginated)
  listItems: NotificationAdminItem[] = [];
  listLoading = false;
  listError = false;
  listFilterOwner = '';
  listFilterRead: 'true' | 'false' | '' = '';
  listFilterFrom = '';
  listFilterTo = '';
  listPageTokens: (string | null)[] = [null]; // stack; [null] = first page
  listCurrentPage = 0;

  constructor(
    private notificationAdminService: NotificationAdminService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  setTab(tab: Tab): void {
    this.activeTab = tab;
    if (tab === 'history') this.loadHistory();
    if (tab === 'list') this.resetAndLoadList();
  }

  async sendBroadcast(): Promise<void> {
    if (!this.broadcastTitle.trim() || !this.broadcastContent.trim()) return;

    this.broadcastSending = true;
    this.broadcastSuccess = false;
    this.broadcastError = '';

    try {
      await this.notificationAdminService.sendBroadcast(
        this.broadcastTitle.trim(),
        this.broadcastContent.trim()
      );
      this.broadcastSuccess = true;
      this.broadcastTitle = '';
      this.broadcastContent = '';
    } catch (e: any) {
      this.broadcastError = e?.error?.message ?? 'Error al enviar el broadcast.';
    } finally {
      this.broadcastSending = false;
      this.cdr.detectChanges();
    }
  }

  async sendIndividual(): Promise<void> {
    if (!this.individualTitle.trim() || !this.individualContent.trim() || !this.individualUserId.trim()) return;

    this.individualSending = true;
    this.individualSuccess = false;
    this.individualError = '';

    try {
      await this.notificationAdminService.sendToUser(
        this.individualTitle.trim(),
        this.individualContent.trim(),
        this.individualUserId.trim()
      );
      this.individualSuccess = true;
      this.individualTitle = '';
      this.individualContent = '';
      this.individualUserId = '';
      this.userSearchQuery = '';
      this.userSearchResults = [];
    } catch (e: any) {
      this.individualError = e?.error?.message ?? 'Error al enviar la notificación.';
    } finally {
      this.individualSending = false;
      this.cdr.detectChanges();
    }
  }

  onUserSearchInput(): void {
    clearTimeout(this.searchDebounceTimer);
    this.userSearchResults = [];
    if (this.userSearchQuery.length < 3) return;
    this.searchDebounceTimer = setTimeout(() => this.doUserSearch(), 400);
  }

  async doUserSearch(): Promise<void> {
    this.userSearchLoading = true;
    try {
      this.userSearchResults = await this.notificationAdminService.searchUsers(this.userSearchQuery);
    } catch {
      this.userSearchResults = [];
    } finally {
      this.userSearchLoading = false;
      this.cdr.detectChanges();
    }
  }

  selectUser(user: UserSearchResult): void {
    this.individualUserId = user.id;
    this.userSearchQuery = user.name || user.email || user.id;
    this.userSearchResults = [];
  }

  clearSelectedUser(): void {
    this.individualUserId = '';
    this.userSearchQuery = '';
    this.userSearchResults = [];
  }

  insertHtml(textareaId: string, field: 'broadcastContent' | 'individualContent', open: string, close: string): void {
    const el = document.getElementById(textareaId) as HTMLTextAreaElement;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = el.value.substring(start, end);
    const replacement = open + (selected || 'texto') + close;
    const newValue = el.value.substring(0, start) + replacement + el.value.substring(end);
    this[field] = newValue;
    setTimeout(() => {
      el.focus();
      const cursor = selected ? start + replacement.length : start + open.length;
      const selEnd = selected ? cursor : cursor + 'texto'.length;
      el.setSelectionRange(cursor, selEnd);
    }, 0);
  }

  insertLink(textareaId: string, field: 'broadcastContent' | 'individualContent'): void {
    this.insertHtml(textareaId, field, '<a href="https://">', '</a>');
  }

  async loadHistory(): Promise<void> {
    this.historyLoading = true;
    this.historyError = false;

    try {
      this.broadcasts = await this.notificationAdminService.getBroadcastHistory();
    } catch (e) {
      console.error('Error loading broadcast history:', e);
      this.historyError = true;
    } finally {
      this.historyLoading = false;
      this.cdr.detectChanges();
    }
  }

  resetAndLoadList(): void {
    this.listPageTokens = [null];
    this.listCurrentPage = 0;
    this.loadList();
  }

  async loadList(token: string | null = null): Promise<void> {
    this.listLoading = true;
    this.listError = false;

    const fromTs = this.listFilterFrom ? new Date(this.listFilterFrom).getTime() : null;
    const toTs = this.listFilterTo ? new Date(this.listFilterTo).setHours(23, 59, 59, 999) : null;

    try {
      const page = await this.notificationAdminService.getAllNotifications({
        owner: this.listFilterOwner.trim() || undefined,
        read: (this.listFilterRead as 'true' | 'false') || null,
        from: fromTs,
        to: toTs ? toTs as number : null,
        nextToken: token,
      });
      this.listItems = page.items;

      // Extend token stack only when navigating forward
      if (token !== null && this.listPageTokens[this.listCurrentPage + 1] == null) {
        this.listPageTokens.push(page.nextToken);
      } else if (token === null) {
        // First page: store next token at index 1
        this.listPageTokens[1] = page.nextToken;
      }
    } catch (e) {
      console.error('Error loading notifications list:', e);
      this.listError = true;
    } finally {
      this.listLoading = false;
      this.cdr.detectChanges();
    }
  }

  listNextPage(): void {
    const nextToken = this.listPageTokens[this.listCurrentPage + 1];
    if (!nextToken) return;
    this.listCurrentPage++;
    this.loadList(nextToken);
  }

  listPrevPage(): void {
    if (this.listCurrentPage === 0) return;
    this.listCurrentPage--;
    const token = this.listPageTokens[this.listCurrentPage] ?? null;
    this.loadList(token === undefined ? null : token);
  }

  applyListFilters(): void {
    this.resetAndLoadList();
  }

  get listHasNextPage(): boolean {
    return !!this.listPageTokens[this.listCurrentPage + 1];
  }

  get listHasPrevPage(): boolean {
    return this.listCurrentPage > 0;
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  stripHtml(html: string): string {
    return html?.replace(/<[^>]*>/g, '') ?? '';
  }
}
